import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  Bookmark, Clock, ArrowLeft, Loader2, Sparkles, BookOpen, AlertTriangle,
  Zap, MessageSquare, Trash2, ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  confused: { label: "I'm Confused", color: 'bg-amber-500/15 text-amber-400 border-amber-500/30', icon: AlertTriangle, dot: 'bg-amber-500', borderColor: '#F59E0B' },
  highlight: { label: 'Highlight', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30', icon: Bookmark, dot: 'bg-blue-500', borderColor: '#3B82F6' },
  important: { label: 'Important', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30', icon: Zap, dot: 'bg-yellow-500', borderColor: '#EAB308' },
  topic: { label: 'New Topic', color: 'bg-purple-500/15 text-purple-400 border-purple-500/30', icon: MessageSquare, dot: 'bg-purple-500', borderColor: '#8B5CF6' },
};

const BookmarkReviewPage = () => {
  const [searchParams] = useSearchParams();
  const lectureId = searchParams.get('lecture');
  const [lecture, setLecture] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiNotes, setAiNotes] = useState('');
  const [generatingNotes, setGeneratingNotes] = useState(false);
  const [activeBookmark, setActiveBookmark] = useState(null);

  useEffect(() => {
    if (!lectureId) return;
    const load = async () => {
      try {
        const [lecRes, bmRes] = await Promise.all([
          axios.get(`${API}/lectures/${lectureId}`),
          axios.get(`${API}/bookmarks/${lectureId}`),
        ]);
        setLecture(lecRes.data);
        setBookmarks(bmRes.data.bookmarks || []);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [lectureId]);

  const generateAINotes = async () => {
    setGeneratingNotes(true);
    try {
      const res = await axios.post(`${API}/bookmarks/${lectureId}/ai-notes`);
      setAiNotes(res.data.notes);
      toast.success(`AI notes generated for ${res.data.confused_count || 0} confused moments`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate notes');
    }
    setGeneratingNotes(false);
  };

  const deleteBookmark = async (id) => {
    try {
      await axios.delete(`${API}/bookmarks/${id}`);
      setBookmarks(prev => prev.filter(b => b.id !== id));
      toast.success('Bookmark removed');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const jumpToTranscript = (bookmark) => {
    setActiveBookmark(bookmark.id);
    // Find the transcript section near this timestamp
    const el = document.getElementById(`transcript-section`);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  if (!lectureId) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20" data-testid="no-lecture-selected">
        <Bookmark className="w-12 h-12 mx-auto text-[var(--isp-muted)] mb-4" />
        <h2 className="text-xl font-bold text-[var(--isp-heading)] mb-2">No Lecture Selected</h2>
        <p className="text-[var(--isp-body)] mb-4">Select a lecture from your library to review its bookmarks.</p>
        <Link to="/lectures"><Button className="rounded-xl bg-[#002FA7] hover:bg-[#00227A]">Go to Lectures</Button></Link>
      </div>
    );
  }

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>;

  const confusedCount = bookmarks.filter(b => b.type === 'confused').length;
  const maxSeconds = bookmarks.length > 0 ? Math.max(...bookmarks.map(b => b.seconds)) : 1;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="bookmark-review-page">
      {/* Header */}
      <div>
        <Link to="/lectures" className="inline-flex items-center gap-1 text-sm text-[#002FA7] hover:underline mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Lectures
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold text-[var(--isp-heading)] flex items-center gap-3">
          <Bookmark className="w-7 h-7 text-[#002FA7]" />
          Bookmark Review
        </h1>
        {lecture && <p className="text-sm text-[var(--isp-body)] mt-1">{lecture.title}</p>}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="bookmark-stats">
        {Object.entries(TYPE_META).map(([type, meta]) => {
          const count = bookmarks.filter(b => b.type === type).length;
          return (
            <div key={type} className="isp-card p-3 text-center">
              <meta.icon className={`w-5 h-5 mx-auto mb-1 ${meta.color.split(' ')[1]}`} />
              <p className="text-xl font-bold text-[var(--isp-heading)]">{count}</p>
              <p className="text-[10px] text-[var(--isp-muted)]">{meta.label}</p>
            </div>
          );
        })}
      </div>

      {/* Timeline */}
      {bookmarks.length > 0 && (
        <div className="isp-card p-5" data-testid="bookmark-timeline">
          <h3 className="text-sm font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#002FA7]" /> Recording Timeline
          </h3>
          <div className="relative h-8 bg-[var(--isp-glass-inner)] rounded-full overflow-hidden border border-[var(--isp-glass-border)]">
            {bookmarks.map((bm, i) => {
              const pct = maxSeconds > 0 ? (bm.seconds / maxSeconds) * 100 : 0;
              const meta = TYPE_META[bm.type] || TYPE_META.highlight;
              return (
                <button
                  key={bm.id || i}
                  onClick={() => jumpToTranscript(bm)}
                  className={`absolute top-1 w-6 h-6 rounded-full ${meta.dot} border-2 border-[var(--isp-card)] shadow-lg hover:scale-125 transition-transform cursor-pointer`}
                  style={{ left: `calc(${Math.min(pct, 96)}% - 12px)` }}
                  title={`${meta.label} at ${bm.timestamp}`}
                  data-testid={`timeline-dot-${i}`}
                />
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-[var(--isp-muted)] mt-1">
            <span>0:00</span>
            <span>{bookmarks[bookmarks.length - 1]?.timestamp || ''}</span>
          </div>
        </div>
      )}

      {/* Bookmark List */}
      <div className="space-y-2" data-testid="bookmark-list">
        {bookmarks.length === 0 ? (
          <div className="isp-card p-8 text-center">
            <Bookmark className="w-10 h-10 mx-auto text-[var(--isp-muted)] mb-3" />
            <p className="text-sm text-[var(--isp-body)]">No bookmarks for this lecture yet.</p>
            <p className="text-xs text-[var(--isp-muted)] mt-1">Record a lecture and use voice commands or buttons to add bookmarks.</p>
          </div>
        ) : (
          bookmarks.map((bm, i) => {
            const meta = TYPE_META[bm.type] || TYPE_META.highlight;
            return (
              <motion.div
                key={bm.id || i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.03 }}
                className={`isp-card p-4 flex items-start gap-3 border-l-4 ${activeBookmark === bm.id ? 'ring-2 ring-[#002FA7]/30' : ''}`}
                style={{ borderLeftColor: meta.borderColor }}
                data-testid={`bookmark-item-${i}`}
              >
                <div className={`p-2 rounded-lg ${meta.color.split(' ').slice(0, 1).join(' ')}`}>
                  <meta.icon className={`w-4 h-4 ${meta.color.split(' ')[1]}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${meta.color}`}>{meta.label}</span>
                    <span className="text-xs text-[var(--isp-muted)] font-mono">{bm.timestamp}</span>
                  </div>
                  {bm.transcript_snippet && (
                    <p className="text-sm text-[var(--isp-body)] line-clamp-2">"{bm.transcript_snippet}"</p>
                  )}
                </div>
                <button onClick={() => deleteBookmark(bm.id)} className="p-1 text-[var(--isp-muted)] hover:text-red-400 transition-colors" title="Delete bookmark">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            );
          })
        )}
      </div>

      {/* AI Notes Section */}
      {confusedCount > 0 && (
        <div className="isp-card p-5" data-testid="ai-notes-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-[var(--isp-heading)] flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" /> AI Study Notes for Confused Moments
            </h3>
            <Button
              onClick={generateAINotes}
              disabled={generatingNotes}
              className="rounded-xl gap-2 text-xs bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              data-testid="generate-ai-notes-btn"
            >
              {generatingNotes ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {generatingNotes ? 'Generating...' : `Explain ${confusedCount} Confused Moment${confusedCount > 1 ? 's' : ''}`}
            </Button>
          </div>

          {aiNotes ? (
            <div className="prose prose-sm max-w-none text-[var(--isp-body)]" data-testid="ai-notes-content">
              <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 whitespace-pre-wrap text-sm leading-relaxed">
                {aiNotes}
              </div>
            </div>
          ) : (
            <p className="text-xs text-[var(--isp-muted)]">
              Click the button above to generate AI explanations for each moment you marked as confusing.
            </p>
          )}
        </div>
      )}

      {/* Transcript Jump Section */}
      {lecture?.transcript && (
        <div className="isp-card p-5" id="transcript-section" data-testid="transcript-section">
          <h3 className="text-sm font-semibold text-[var(--isp-heading)] flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4 text-[#002FA7]" /> Lecture Transcript
          </h3>
          <div className="max-h-96 overflow-y-auto text-sm text-[var(--isp-body)] leading-relaxed whitespace-pre-wrap rounded-xl p-4 bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)]">
            {lecture.transcript}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookmarkReviewPage;
